import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, withCtx as _withCtx, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-9c664a96"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "space-y-5" }
const _hoisted_2 = {
  key: 0,
  class: "p-4 text-center text-gray-500 border border-gray-200 rounded-lg"
}
const _hoisted_3 = {
  key: 1,
  class: "p-4 text-red-700 bg-red-50 border border-red-200 rounded-lg"
}
const _hoisted_4 = ["disabled"]
const _hoisted_5 = {
  key: 0,
  class: "p-4 text-green-700 bg-green-50 border border-green-200 rounded-lg"
}
const _hoisted_6 = { key: 0 }
const _hoisted_7 = ["innerHTML"]
const _hoisted_8 = { class: "mt-3 text-sm text-gray-500" }
const _hoisted_9 = { class: "space-y-3" }
const _hoisted_10 = { class: "p-3 bg-gray-50 rounded-lg" }
const _hoisted_11 = { class: "text-sm text-gray-500" }
const _hoisted_12 = { class: "font-medium" }
const _hoisted_13 = { class: "p-3 bg-gray-50 rounded-lg" }
const _hoisted_14 = { class: "text-sm text-gray-500" }
const _hoisted_15 = { class: "p-3 bg-gray-50 rounded-lg" }
const _hoisted_16 = { class: "text-sm text-gray-500" }
const _hoisted_17 = { class: "mt-1 list-disc list-inside" }
const _hoisted_18 = {
  key: 0,
  class: "text-sm text-gray-500"
}
const _hoisted_19 = {
  key: 1,
  class: "text-sm text-amber-700"
}
const _hoisted_20 = { class: "flex flex-wrap gap-3" }
const _hoisted_21 = ["href"]
const _hoisted_22 = ["disabled"]
const _hoisted_23 = ["disabled"]

import {
  computed,
  onBeforeUnmount,
  ref,
  watch,
} from "vue";
import Modal from "@/components/common/Modal.vue";
import { ProcessUserDataService } from "@/services";
import { getLocale, t } from "@/i18n";

type MobileSignStatus =
  | "CREATED"
  | "WAITING_FOR_SIGN"
  | "CALLBACK_RECEIVED"
  | "SIGNED"
  | "FAILED"
  | "EXPIRED"
  | "CANCELLED";

export interface MobileSignInfo {
  processUserId: string;
  guid: string;
  urlFl?: string;
  urlQr?: string;
  qrSvg?: string;
  expiresAt?: string;
  signedAt?: string;
  lastError?: string;
  status: MobileSignStatus;
  fileNames: string[];
}


export default /*#__PURE__*/_defineComponent({
  props: {
  show: {
    type: Boolean,
    default: false,
  },
  processUserId: {
    type: String,
    required: true,
  },
},
  emits: [
  "close",
  "session-started",
  "session-ready",
  "session-updated",
  "terminal",
  "completed",
  "cancelled",
],
  setup(__props, { emit }) {

const props = __props





const mobileSignInfo = ref<MobileSignInfo | null>(null);
const isLoading = ref(false);
const isRefreshing = ref(false);
const isCancelling = ref(false);
const errorMessage = ref("");
const isTransientVisibleError = ref(false);
let pollingTimer: number | undefined;
let disposed = false;
let requestGeneration = 0;
let autoOpenedDeepLink = false;

const terminalStatuses: MobileSignStatus[] = [
  "SIGNED",
  "FAILED",
  "EXPIRED",
  "CANCELLED",
];
const waitingStatuses: MobileSignStatus[] = [
  "CREATED",
  "WAITING_FOR_SIGN",
  "CALLBACK_RECEIVED",
];

const isMobileDevice = /Android|iPhone|iPad|iPod|Mobile/i.test(
  typeof navigator !== "undefined" ? navigator.userAgent : ""
);

const statusLabels: Record<MobileSignStatus, string> = {
  CREATED: t("mobileSign.status.created"),
  WAITING_FOR_SIGN: t("mobileSign.status.waiting"),
  CALLBACK_RECEIVED: t("mobileSign.status.callback"),
  SIGNED: t("mobileSign.status.signed"),
  FAILED: t("mobileSign.status.failed"),
  EXPIRED: t("mobileSign.status.expired"),
  CANCELLED: t("mobileSign.status.cancelled"),
};

const statusLabel = computed(() => {
  if (!mobileSignInfo.value) return "-";
  return statusLabels[mobileSignInfo.value.status] || mobileSignInfo.value.status;
});

const formattedExpiresAt = computed(() => {
  if (!mobileSignInfo.value?.expiresAt) return "-";
  return new Date(mobileSignInfo.value.expiresAt).toLocaleString(
    getLocale() === "ru" ? "ru-RU" : "en-GB"
  );
});

const canRetry = computed(() =>
  ["FAILED", "EXPIRED"].includes(mobileSignInfo.value?.status || "")
);

const isTerminalStatus = computed(() => {
  const status = mobileSignInfo.value?.status;
  return !!status && terminalStatuses.includes(status);
});

const getErrorMessage = (
  error: any,
  fallbackKey = "mobileSign.requestError"
) =>
  error?.response?.data?.message ||
  error?.response?.data?.data ||
  error?.message ||
  t(fallbackKey);

const isTransientPollError = (message: string) =>
  /ReadTimeout|timeout|Failed to fetch|NetworkError|AbortError|ECONNRESET|Gateway Timeout|504|502/i.test(
    message || ""
  );

const isSessionReady = (info: MobileSignInfo) => {
  if (!waitingStatuses.includes(info.status)) return false;
  if (isMobileDevice) return !!info.urlFl;
  return !!(info.qrSvg || info.urlQr || info.urlFl);
};

const stopPolling = () => {
  if (pollingTimer !== undefined) {
    window.clearInterval(pollingTimer);
    pollingTimer = undefined;
  }
};

const maybeOpenDeepLink = (info: MobileSignInfo) => {
  if (!isMobileDevice || autoOpenedDeepLink || !info.urlFl) return;
  if (!waitingStatuses.includes(info.status)) return;
  autoOpenedDeepLink = true;
  window.location.href = info.urlFl;
};

const handleInfo = (info: MobileSignInfo) => {
  if (disposed) return;
  mobileSignInfo.value = info;
  emit("session-updated", info);

  if (info.lastError && terminalStatuses.includes(info.status)) {
    errorMessage.value = info.lastError;
    isTransientVisibleError.value = false;
  } else if (!terminalStatuses.includes(info.status)) {
    errorMessage.value = "";
    isTransientVisibleError.value = false;
  }

  if (isSessionReady(info)) {
    emit("session-ready", info);
    maybeOpenDeepLink(info);
  }

  if (terminalStatuses.includes(info.status)) {
    stopPolling();
    emit("terminal", info.status);
    if (info.status === "SIGNED") {
      emit("completed");
    }
  }
};

const refreshStatus = async () => {
  if (!props.processUserId || isRefreshing.value) return;
  const generation = requestGeneration;
  isRefreshing.value = true;
  try {
    const response = await ProcessUserDataService.getMobileSignStatus(
      props.processUserId
    );
    if (disposed || generation !== requestGeneration) return;
    handleInfo(response.data.data);
  } catch (error) {
    if (disposed || generation !== requestGeneration) return;
    const message = getErrorMessage(error);
    if (!isTransientPollError(message)) {
      errorMessage.value = message;
      isTransientVisibleError.value = false;
    }
  } finally {
    isRefreshing.value = false;
  }
};

const startPolling = () => {
  if (disposed) return;
  stopPolling();
  pollingTimer = window.setInterval(refreshStatus, 3000);
};

const initMobileSign = async () => {
  if (!props.processUserId || isLoading.value || isRefreshing.value) return;
  requestGeneration += 1;
  const generation = requestGeneration;
  isLoading.value = true;
  errorMessage.value = "";
  isTransientVisibleError.value = false;
  mobileSignInfo.value = null;
  autoOpenedDeepLink = false;
  try {
    const response = await ProcessUserDataService.initMobileSign(
      props.processUserId
    );
    if (disposed || generation !== requestGeneration) return;
    handleInfo(response.data.data);
    if (!terminalStatuses.includes(response.data.data.status)) {
      emit("session-started", response.data.data);
      startPolling();
    }
  } catch (error) {
    if (disposed || generation !== requestGeneration) return;
    errorMessage.value = getErrorMessage(error);
    emit("terminal", "FAILED");
  } finally {
    if (generation === requestGeneration) {
      isLoading.value = false;
    }
  }
};

const requestClose = async () => {
  if (isLoading.value || isCancelling.value) return;

  const status = mobileSignInfo.value?.status;
  if (status && terminalStatuses.includes(status)) {
    emit("close");
    return;
  }
  if (status === "CALLBACK_RECEIVED") {
    errorMessage.value = t("mobileSign.cancelProcessing");
    isTransientVisibleError.value = true;
    return;
  }
  if (!window.confirm(t("mobileSign.cancelConfirm"))) return;

  requestGeneration += 1;
  stopPolling();
  isCancelling.value = true;
  errorMessage.value = "";
  isTransientVisibleError.value = false;
  try {
    await ProcessUserDataService.cancelMobileSign(props.processUserId);
    mobileSignInfo.value = null;
    emit("cancelled");
  } catch (error) {
    errorMessage.value = getErrorMessage(error, "mobileSign.cancelError");
    isTransientVisibleError.value = true;
    if (mobileSignInfo.value && !isTerminalStatus.value) {
      startPolling();
    }
  } finally {
    isCancelling.value = false;
  }
};

const restoreOrInit = async () => {
  if (!props.processUserId || isLoading.value) return;
  requestGeneration += 1;
  const generation = requestGeneration;
  isLoading.value = true;
  errorMessage.value = "";
  try {
    const activeResponse = await ProcessUserDataService.getActiveMobileSign(
      props.processUserId
    );
    if (disposed || generation !== requestGeneration) return;
    const active = activeResponse.data.data;
    if (active) {
      handleInfo(active);
      if (!terminalStatuses.includes(active.status)) {
        emit("session-started", active);
        startPolling();
      }
      isLoading.value = false;
      return;
    }
  } catch (_) {
    // Fall through to create a new session.
  }

  if (disposed || generation !== requestGeneration) return;
  isLoading.value = false;
  await initMobileSign();
};

watch(
  () => props.show,
  (show) => {
    if (show) {
      if (!mobileSignInfo.value || isTerminalStatus.value) {
        restoreOrInit();
      } else if (!terminalStatuses.includes(mobileSignInfo.value.status)) {
        startPolling();
      }
    } else {
      stopPolling();
    }
  }
);

onBeforeUnmount(() => {
  disposed = true;
  stopPolling();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(Modal, {
    show: __props.show,
    title: _unref(t)('mobileSign.title'),
    onClose: requestClose
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        (isLoading.value || isCancelling.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, _toDisplayString(isCancelling.value
            ? _unref(t)("mobileSign.cancelling")
            : _unref(t)("mobileSign.creating")), 1))
          : (errorMessage.value && !mobileSignInfo.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                _createElementVNode("p", null, _toDisplayString(errorMessage.value), 1),
                _createElementVNode("button", {
                  type: "button",
                  class: "px-4 py-2 mt-3 text-gray-800 bg-white border border-gray-300 rounded-lg hover:bg-gray-100",
                  disabled: isRefreshing.value,
                  onClick: initMobileSign
                }, _toDisplayString(_unref(t)("mobileSign.retry")), 9, _hoisted_4)
              ]))
            : (mobileSignInfo.value)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                  (mobileSignInfo.value.status === 'SIGNED')
                    ? (_openBlock(), _createElementBlock("div", _hoisted_5, _toDisplayString(_unref(t)("mobileSign.success")), 1))
                    : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                        _createElementVNode("div", {
                          class: _normalizeClass(["grid gap-6", _unref(isMobileDevice) ? 'grid-cols-1' : 'md:grid-cols-2'])
                        }, [
                          (!_unref(isMobileDevice) && !_unref(isTerminalStatus))
                            ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                                (mobileSignInfo.value.qrSvg)
                                  ? (_openBlock(), _createElementBlock("div", {
                                      key: 0,
                                      class: "flex items-center justify-center p-4 border border-gray-200 rounded-lg mobile-sign-qr",
                                      innerHTML: mobileSignInfo.value.qrSvg
                                    }, null, 8, _hoisted_7))
                                  : _createCommentVNode("", true),
                                _createElementVNode("p", _hoisted_8, _toDisplayString(_unref(t)("mobileSign.scanQr")), 1)
                              ]))
                            : _createCommentVNode("", true),
                          _createElementVNode("div", _hoisted_9, [
                            _createElementVNode("div", _hoisted_10, [
                              _createElementVNode("p", _hoisted_11, _toDisplayString(_unref(t)("mobileSign.status")), 1),
                              _createElementVNode("p", _hoisted_12, _toDisplayString(_unref(statusLabel)), 1)
                            ]),
                            _createElementVNode("div", _hoisted_13, [
                              _createElementVNode("p", _hoisted_14, _toDisplayString(_unref(t)("mobileSign.expiresAt")), 1),
                              _createElementVNode("p", null, _toDisplayString(_unref(formattedExpiresAt)), 1)
                            ]),
                            _createElementVNode("div", _hoisted_15, [
                              _createElementVNode("p", _hoisted_16, _toDisplayString(_unref(t)("mobileSign.documents")), 1),
                              _createElementVNode("ul", _hoisted_17, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(mobileSignInfo.value.fileNames, (fileName) => {
                                  return (_openBlock(), _createElementBlock("li", {
                                    class: "mobile-sign-file-name",
                                    key: fileName
                                  }, _toDisplayString(fileName), 1))
                                }), 128))
                              ])
                            ]),
                            (_unref(isMobileDevice) && !_unref(isTerminalStatus))
                              ? (_openBlock(), _createElementBlock("p", _hoisted_18, _toDisplayString(_unref(t)("mobileSign.openHint")), 1))
                              : _createCommentVNode("", true),
                            (errorMessage.value && isTransientVisibleError.value)
                              ? (_openBlock(), _createElementBlock("p", _hoisted_19, _toDisplayString(errorMessage.value), 1))
                              : _createCommentVNode("", true)
                          ])
                        ], 2),
                        _createElementVNode("div", _hoisted_20, [
                          (_unref(isMobileDevice) && mobileSignInfo.value.urlFl && !_unref(isTerminalStatus))
                            ? (_openBlock(), _createElementBlock("a", {
                                key: 0,
                                href: mobileSignInfo.value.urlFl,
                                class: "px-4 py-2 text-white bg-green-600 rounded-lg hover:bg-green-700"
                              }, _toDisplayString(_unref(t)("mobileSign.open")), 9, _hoisted_21))
                            : _createCommentVNode("", true),
                          (!_unref(isTerminalStatus))
                            ? (_openBlock(), _createElementBlock("button", {
                                key: 1,
                                type: "button",
                                class: "px-4 py-2 text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-100",
                                disabled: isRefreshing.value,
                                onClick: refreshStatus
                              }, _toDisplayString(isRefreshing.value
                  ? _unref(t)("mobileSign.checking")
                  : _unref(t)("mobileSign.checkStatus")), 9, _hoisted_22))
                            : _createCommentVNode("", true),
                          (_unref(canRetry))
                            ? (_openBlock(), _createElementBlock("button", {
                                key: 2,
                                type: "button",
                                class: "px-4 py-2 text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-100",
                                disabled: isRefreshing.value,
                                onClick: initMobileSign
                              }, _toDisplayString(_unref(t)("mobileSign.newSession")), 9, _hoisted_23))
                            : _createCommentVNode("", true)
                        ])
                      ], 64))
                ], 64))
              : _createCommentVNode("", true)
      ])
    ]),
    _: 1
  }, 8, ["show", "title"]))
}
}

})